import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import GeneriqueModal from "components/GeneriqueModal";
import ButtonCancel from "components/buttons/ButtonCancel";
import ButtonGreen from "components/buttons/ButtonGreen";
import { useImportEcrituresMutation } from "actions/comptabilite/mutations";
import {
  downloadEcrituresExcelTemplate,
  downloadEcrituresCsvTemplate,
} from "utils/excelTemplateEcritures";

const ModalImporterEcritures = ({ isOpen, onClose, dossier_id, onSuccess }) => {
  const [importEcritures, { isLoading }] = useImportEcrituresMutation();
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [resultErrors, setResultErrors] = useState([]);
  const fileInputRef = useRef(null);

  const handleFileChange = (file) => {
    if (!file) return;
    const name = file.name.toLowerCase();
    if (
      !name.endsWith(".xlsx") &&
      !name.endsWith(".xlsm") &&
      !name.endsWith(".xltx") &&
      !name.endsWith(".csv") &&
      !name.endsWith(".txt")
    ) {
      toast.error("Format non supporté. Veuillez choisir un fichier Excel (.xlsx) ou CSV (.csv).");
      return;
    }
    setSelectedFile(file);
    setResultErrors([]);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileChange(e.dataTransfer.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      toast.error("Veuillez sélectionner un fichier à importer.");
      return;
    }
    if (!dossier_id) {
      toast.error("Dossier introuvable.");
      return;
    }

    const formData = new FormData();
    formData.append("dossier_id", dossier_id);
    formData.append("file", selectedFile);

    try {
      const res = await importEcritures(formData).unwrap();
      const nbImportes = res?.importes ?? 0;
      const nbExistant = res?.deja_existants ?? 0;
      const hasErrors = Array.isArray(res?.erreurs) && res.erreurs.length > 0;

      // Dès qu'au moins une écriture a été enregistrée ou reconnue, actualiser la liste parente
      if ((nbImportes > 0 || nbExistant > 0) && onSuccess) {
        onSuccess();
      }

      if (hasErrors) {
        setResultErrors(res.erreurs);
        if (nbImportes > 0) {
          toast.warning(
            res?.message ||
              `Import partiel : ${nbImportes} écriture(s) importée(s), certaines lignes comportent des anomalies.`
          );
        } else {
          toast.error(res?.message || "Aucune écriture valide n'a pu être importée.");
        }
      } else {
        toast.success(res?.message || "Écritures importées avec succès !");
        handleClose();
      }
    } catch (error) {
      const errData = error?.data;
      const msg = errData?.message || "Erreur lors de l'import des écritures.";
      toast.error(msg);
      if (errData?.importes > 0 && onSuccess) {
        onSuccess();
      }
      if (Array.isArray(errData?.erreurs)) {
        setResultErrors(errData.erreurs);
      }
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setResultErrors([]);
    onClose();
  };

  return (
    <GeneriqueModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Importer des écritures comptables"
    >
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {/* Bandeau d'aide & Téléchargement des modèles */}
        <div
          style={{
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            borderRadius: 8,
            padding: "14px 16px",
            fontSize: 13,
            color: "#334155",
          }}
        >
          <div
            style={{
              fontWeight: 600,
              marginBottom: 6,
              color: "#0f172a",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#009688" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
            Formats et modèles acceptés
          </div>
          <p style={{ margin: "0 0 12px 0", color: "#64748b", lineHeight: 1.5 }}>
            Importez directement vos journaux comptables (Caisse, Banque, Achats, Salaires, etc.)
            au format standard Sage / SYSCOHADA (9 colonnes) ou sous forme d&apos;écritures simples.
            Toutes les classes de comptes (1 à 7) sont prises en compte automatiquement.
          </p>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={downloadEcrituresExcelTemplate}
              style={{
                background: "#009688",
                color: "#ffffff",
                border: "none",
                borderRadius: 6,
                padding: "7px 14px",
                fontSize: 12,
                fontWeight: 500,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                transition: "opacity 0.2s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.9")}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Modèle Excel (.xlsx)
            </button>

            <button
              type="button"
              onClick={downloadEcrituresCsvTemplate}
              style={{
                background: "#ffffff",
                color: "#0f172a",
                border: "1px solid #cbd5e1",
                borderRadius: 6,
                padding: "7px 14px",
                fontSize: 12,
                fontWeight: 500,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                transition: "background 0.2s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#f1f5f9")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "#ffffff")}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
              </svg>
              Modèle CSV
            </button>
          </div>
        </div>

        {/* Zone de Drag & Drop */}
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          style={{
            border: `2px dashed ${dragActive ? "#009688" : "#cbd5e1"}`,
            background: dragActive ? "#f0f9fa" : "#fafafa",
            borderRadius: 10,
            padding: "28px 16px",
            textAlign: "center",
            cursor: "pointer",
            transition: "all 0.2s ease",
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xlsm,.xltx,.csv,.txt"
            style={{ display: "none" }}
            onChange={(e) => handleFileChange(e.target.files?.[0])}
          />
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 10 }}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
          </div>
          {selectedFile ? (
            <div>
              <div style={{ fontWeight: 600, color: "#009688", fontSize: 14 }}>
                {selectedFile.name}
              </div>
              <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                {(selectedFile.size / 1024).toFixed(1)} Ko : Cliquez pour changer de fichier
              </div>
            </div>
          ) : (
            <div>
              <div style={{ fontWeight: 500, color: "#0f172a", fontSize: 14 }}>
                Glissez votre fichier ici ou cliquez pour parcourir
              </div>
              <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                Formats acceptés : Excel (.xlsx) ou CSV (.csv)
              </div>
            </div>
          )}
        </div>

        {/* Rapport d'erreurs éventuelles */}
        {resultErrors.length > 0 && (
          <div
            style={{
              background: "#fff1f2",
              border: "1px solid #fecdd3",
              borderRadius: 8,
              padding: "10px 14px",
              fontSize: 12,
              color: "#9f1239",
              maxHeight: 140,
              overflowY: "auto",
            }}
          >
            <strong>Attention, certaines lignes ont été rejetées :</strong>
            <ul style={{ margin: "6px 0 0 16px", padding: 0 }}>
              {resultErrors.map((err, i) => (
                <li key={i}>{err}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Boutons d'actions */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 10 }}>
          <ButtonCancel onClick={handleClose} disabled={isLoading} text="Annuler" />
          <ButtonGreen
            type="submit"
            disabled={isLoading || !selectedFile}
            text={isLoading ? "Import en cours..." : "Importer les écritures"}
          />
        </div>
      </form>
    </GeneriqueModal>
  );
};

export default ModalImporterEcritures;
